/**
 * Service for managing user contacts relationships
 * Uses the Contacts API - contacts are stored in the Contacts table
 *
 * New structure:
 * - localuuid: unique identifier for localStorage (generated client-side)
 * - peopleuuid: reference to peoples table (optional - null for private contacts)
 * - contactType: 'PUBLIC' if peopleuuid is set, 'CONTACT' otherwise
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/contacts';
const STORAGE_KEY = 'cabaza_user_contacts';

// In-memory cache
let contactsCache = {};

// Debounce timer for server sync
let syncDebounceTimer = null;
const SYNC_DEBOUNCE_MS = 2000; // 2 seconds debounce

/**
 * Generate a UUID v4
 * @returns {string} UUID
 */
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Get contacts from localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @returns {Object} { contacts: Array, lastModified: string|null }
 */
const getLocalContactsWithTimestamp = (peopleId) => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        const userData = allData[peopleId];

        // Support both old format (array) and new format (object with timestamp)
        if (Array.isArray(userData)) {
            return { contacts: userData, lastModified: null };
        } else if (userData && typeof userData === 'object') {
            return {
                contacts: userData.contacts || [],
                lastModified: userData.lastModified || null
            };
        }
        return { contacts: [], lastModified: null };
    } catch (error) {
        console.error('Error getting local contacts:', error);
        return { contacts: [], lastModified: null };
    }
};

/**
 * Get contacts from localStorage (fallback) - backward compatible
 * @param {number} peopleId - User's ID
 * @returns {Array}
 */
const getLocalContacts = (peopleId) => {
    return getLocalContactsWithTimestamp(peopleId).contacts;
};

/**
 * Save contacts to localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @param {Array} contacts - Contacts array
 * @param {string} lastModified - ISO timestamp (optional, defaults to now)
 */
const saveLocalContacts = (peopleId, contacts, lastModified = null) => {
    try {
        const timestamp = lastModified || new Date().toISOString();
        console.log('=== SAVING TO LOCALSTORAGE ===');
        console.log('peopleId:', peopleId);
        console.log('contacts count:', contacts.length);
        console.log('timestamp:', timestamp);

        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        allData[peopleId] = {
            contacts: contacts,
            lastModified: timestamp
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

        // Verify save
        const verify = localStorage.getItem(STORAGE_KEY);
        const verifyData = JSON.parse(verify);
        console.log('Verified localStorage save:', verifyData[peopleId]?.contacts?.length, 'contacts');
    } catch (error) {
        console.error('Error saving local contacts:', error);
    }
};

/**
 * Get local timestamp for a user
 * @param {number} peopleId - User's ID
 * @returns {string|null}
 */
const getLocalTimestamp = (peopleId) => {
    return getLocalContactsWithTimestamp(peopleId).lastModified;
};

/**
 * Get contacts count from database
 * @param {number} userId - User's ID
 * @returns {Promise<number>} Count of contacts in DB
 */
const getContactsCountFromDB = async (userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/count/${userId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const count = result.count || result.COUNT || 0;

        return success ? count : 0;
    } catch (error) {
        console.error('Error getting contacts count from DB:', error);
        return -1; // Return -1 to indicate error
    }
};

/**
 * Get current user's peopleUuid and storageKey from localStorage
 * @returns {Object} { peopleuuid, storageKey } or null
 */
const getCurrentUserStorageInfo = () => {
    try {
        const userJson = localStorage.getItem('cabaza_user');
        if (userJson) {
            const user = JSON.parse(userJson);
            return {
                peopleuuid: user.peopleUuid || user.peopleuuid || user.PEOPLEUUID,
                storageKey: user.storageKey || user.STORAGEKEY
            };
        }
    } catch (error) {
        console.error('Error getting user storage info:', error);
    }
    return null;
};

/**
 * Sync contacts TO server (debounced)
 * Saves current localStorage contacts to encrypted file on server
 * @param {number} userId - User's ID
 */
const syncContactsToServerDebounced = (userId) => {
    // Clear existing timer
    if (syncDebounceTimer) {
        clearTimeout(syncDebounceTimer);
    }

    // Set new timer
    syncDebounceTimer = setTimeout(async () => {
        const storageInfo = getCurrentUserStorageInfo();
        if (!storageInfo?.peopleuuid || !storageInfo?.storageKey) {
            console.warn('Cannot sync to server: missing peopleuuid or storageKey');
            return;
        }

        try {
            const localData = getLocalContactsWithTimestamp(userId);
            const timestamp = localData.lastModified || new Date().toISOString();
            console.log('Syncing contacts to server...', localData.contacts.length, 'contacts, timestamp:', timestamp);

            const result = await saveContactsToFile(
                storageInfo.peopleuuid,
                storageInfo.storageKey,
                localData.contacts,
                timestamp
            );

            if (result.success || result.SUCCESS) {
                console.log('Contacts synced to server successfully');
            } else {
                console.error('Failed to sync contacts to server:', result.message || result.MESSAGE);
            }
        } catch (error) {
            console.error('Error syncing contacts to server:', error);
        }
    }, SYNC_DEBOUNCE_MS);
};

/**
 * Sync contacts with DB to check for deleted/private profiles and reassociate orphan contacts
 * Called after login to:
 * 1. Check if linked profiles still exist (remove peopleuuid if deleted)
 * 2. Check if profiles became PRIVATE (contactType will be set from role)
 * 3. Reassociate orphan contacts if a matching profile is found (by name + email)
 * @param {number} userId - User's ID
 * @param {Array} contacts - Contacts array to sync
 * @returns {Promise<Array>} Synced contacts with updated flags
 */
export const syncContactsWithDB = async (userId, contacts) => {
    if (!userId || !contacts || contacts.length === 0) {
        return contacts;
    }

    try {
        console.log('Syncing contacts with DB to check profiles...', contacts.length, 'contacts');

        const response = await fetch(`${API_BASE_URL}/sync`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                userId,
                contacts
            })
        });

        if (!response.ok) {
            console.error('Sync API error:', response.status);
            return contacts;
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const syncedContacts = result.contacts || result.CONTACTS || contacts;
        const changes = result.changes || result.CHANGES || 0;

        if (success) {
            console.log(`Sync completed: ${changes} changes`);
            if (changes > 0) {
                // Update localStorage with synced contacts
                const timestamp = new Date().toISOString();
                saveLocalContacts(userId, syncedContacts, timestamp);
                contactsCache[userId] = syncedContacts;

                // Also sync to server file
                const storageInfo = getCurrentUserStorageInfo();
                if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                    await saveContactsToFile(
                        storageInfo.peopleuuid,
                        storageInfo.storageKey,
                        syncedContacts,
                        timestamp
                    );
                }
            }
            return syncedContacts;
        }

        return contacts;
    } catch (error) {
        console.error('Error syncing contacts with DB:', error);
        return contacts;
    }
};

/**
 * Sync contacts FROM server on login
 * Validates against DB count, then uses most recent source that matches
 * If no file exists, loads from API and creates the file
 * Also syncs with DB to check for deleted/private profiles
 * @param {number} userId - User's ID
 * @param {string} peopleuuid - User's peopleUuid
 * @param {string} storageKey - User's storageKey for decryption
 * @returns {Promise<Array>} Loaded contacts
 */
export const syncContactsFromServer = async (userId, peopleuuid, storageKey) => {
    if (!userId || !peopleuuid || !storageKey) {
        console.warn('Cannot sync from server: missing userId, peopleuuid, or storageKey');
        return [];
    }

    try {
        console.log('Syncing contacts - validating against DB count...');

        // Get DB count, local and server data
        const [dbCount, localData, serverData] = await Promise.all([
            getContactsCountFromDB(userId),
            Promise.resolve(getLocalContactsWithTimestamp(userId)),
            loadContactsFromFileWithTimestamp(peopleuuid, storageKey)
        ]);

        const localTimestamp = localData.lastModified ? new Date(localData.lastModified) : null;
        const serverTimestamp = serverData.lastModified ? new Date(serverData.lastModified) : null;

        console.log('DB count:', dbCount);
        console.log('Local:', localData.contacts.length, 'contacts, timestamp:', localData.lastModified);
        console.log('Server:', serverData.contacts.length, 'contacts, timestamp:', serverData.lastModified);

        // Check which sources match the DB count
        const localMatchesDB = localData.contacts.length === dbCount;
        const serverMatchesDB = serverData.contacts.length === dbCount;

        console.log('Local matches DB:', localMatchesDB, '| Server matches DB:', serverMatchesDB);

        // Priority: Most recent source that matches DB count
        let contactsToSync = null;

        if (localMatchesDB && serverMatchesDB) {
            // Both match DB - use the most recent
            if (localTimestamp && serverTimestamp) {
                if (localTimestamp >= serverTimestamp) {
                    console.log('Both match DB, local is newer or equal - using local');
                    contactsCache[userId] = localData.contacts;
                    if (localTimestamp > serverTimestamp) {
                        await saveContactsToFile(peopleuuid, storageKey, localData.contacts, localData.lastModified);
                    }
                    contactsToSync = localData.contacts;
                } else {
                    console.log('Both match DB, server is newer - using server');
                    contactsCache[userId] = serverData.contacts;
                    saveLocalContacts(userId, serverData.contacts, serverData.lastModified);
                    contactsToSync = serverData.contacts;
                }
            } else {
                // No timestamps - prefer local
                console.log('Both match DB, no timestamps - using local');
                contactsCache[userId] = localData.contacts;
                const timestamp = new Date().toISOString();
                saveLocalContacts(userId, localData.contacts, timestamp);
                await saveContactsToFile(peopleuuid, storageKey, localData.contacts, timestamp);
                contactsToSync = localData.contacts;
            }
        } else if (localMatchesDB) {
            // Only local matches DB - use local and sync to server
            console.log('Only local matches DB - using local and syncing to server');
            contactsCache[userId] = localData.contacts;
            const timestamp = localData.lastModified || new Date().toISOString();
            await saveContactsToFile(peopleuuid, storageKey, localData.contacts, timestamp);
            contactsToSync = localData.contacts;
        } else if (serverMatchesDB) {
            // Only server matches DB - use server and update local
            console.log('Only server matches DB - using server and updating local');
            contactsCache[userId] = serverData.contacts;
            saveLocalContacts(userId, serverData.contacts, serverData.lastModified || new Date().toISOString());
            contactsToSync = serverData.contacts;
        } else if (dbCount === 0) {
            // DB is empty - but PROTECT FileServer if it has data
            // This prevents accidental data loss if localStorage was cleared
            if (serverData.contacts.length > 0) {
                console.log('DB is empty but FileServer has', serverData.contacts.length, 'contacts - PROTECTING FileServer data');
                // Use FileServer data and restore to local
                contactsCache[userId] = serverData.contacts;
                saveLocalContacts(userId, serverData.contacts, serverData.lastModified || new Date().toISOString());
                // Sync with DB to restore contacts
                return await syncContactsWithDB(userId, serverData.contacts);
            }
            // FileServer is also empty - safe to clear
            console.log('DB and FileServer both empty - clearing local');
            contactsCache[userId] = [];
            const timestamp = new Date().toISOString();
            saveLocalContacts(userId, [], timestamp);
            return []; // No need to sync empty list, don't touch FileServer
        } else {
            // Neither matches DB - reload from API (source of truth)
            console.log('Neither matches DB count, reloading from API...');

            const response = await fetch(`${API_BASE_URL}/owner/${userId}`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            if (response.ok) {
                const result = await response.json();
                const data = result.data || result.DATA || [];

                const contacts = data.map(c => normalizeContact(c));
                const timestamp = new Date().toISOString();
                console.log('Loaded', contacts.length, 'contacts from API (source of truth)');

                // Update everything
                contactsCache[userId] = contacts;
                saveLocalContacts(userId, contacts, timestamp);
                await saveContactsToFile(peopleuuid, storageKey, contacts, timestamp);

                contactsToSync = contacts;
            }
        }

        // Sync with DB to check for deleted/private profiles
        if (contactsToSync && contactsToSync.length > 0) {
            return await syncContactsWithDB(userId, contactsToSync);
        } else if (contactsToSync) {
            return contactsToSync;
        }

        // Fallback to most recent if API fails (contactsToSync is null)
        console.log('API failed, using most recent source');
        let fallbackContacts;
        if (localTimestamp && serverTimestamp) {
            if (localTimestamp >= serverTimestamp) {
                contactsCache[userId] = localData.contacts;
                fallbackContacts = localData.contacts;
            } else {
                contactsCache[userId] = serverData.contacts;
                fallbackContacts = serverData.contacts;
            }
        } else {
            fallbackContacts = localData.contacts.length > 0 ? localData.contacts : serverData.contacts;
        }
        // Sync with DB to check for deleted/private profiles
        if (fallbackContacts.length > 0) {
            return await syncContactsWithDB(userId, fallbackContacts);
        }
        return fallbackContacts;
    } catch (error) {
        console.error('Error syncing contacts from server:', error);
        return getLocalContacts(userId);
    }
};

/**
 * Normalize contact data from API
 * Table Contacts: id, localuuid, peopleuuid, createdById, createdAt, rating
 * Contact info comes from Peoples table via peopleuuid join (LEFT JOIN)
 * contactType: 'PUBLIC' if peopleuuid is set, 'CONTACT' otherwise
 * @param {Object} contact - Raw contact from API
 * @returns {Object} Normalized contact
 */
const normalizeContact = (contact) => ({
    // Contact table fields
    id: contact.id || contact.ID,
    localuuid: contact.localuuid || contact.LOCALUUID,
    peopleuuid: contact.peopleuuid || contact.PEOPLEUUID || null,
    createdById: contact.createdbyid || contact.createdById || contact.CREATEDBYID,
    rating: contact.rating || contact.RATING || 0,
    createdAt: contact.createdat || contact.createdAt || contact.CREATEDAT,
    // Computed field - use role from linked profile if peopleuuid exists
    contactType: (contact.peopleuuid || contact.PEOPLEUUID)
        ? (contact.role || contact.ROLE || 'PUBLIC')
        : 'CONTACT',
    // Basic info fields
    firstName: contact.firstname || contact.firstName || contact.FIRSTNAME || '',
    lastName: contact.lastname || contact.lastName || contact.LASTNAME || '',
    fullName: contact.fullname || contact.fullName || contact.FULLNAME || '',
    sexe: contact.sexe || contact.SEXE || '',
    email: contact.email || contact.EMAIL || '',
    phone: contact.phone || contact.PHONE || '',
    phoneBureau: contact.phonebureau || contact.phoneBureau || contact.PHONEBUREAU || '',
    gsm: contact.gsm || contact.GSM || '',
    company: contact.company || contact.COMPANY || '',
    position: contact.position || contact.POSITION || '',
    role: contact.role || contact.ROLE || '',
    website: contact.website || contact.WEBSITE || '',
    description: contact.description || contact.DESCRIPTION || '',
    // Address fields (detailed)
    googleAddress: contact.googleaddress || contact.googleAddress || contact.GOOGLEADDRESS || '',
    street: contact.street || contact.STREET || '',
    number: contact.number || contact.NUMBER || '',
    box: contact.box || contact.BOX || '',
    postalCode: contact.postalcode || contact.postalCode || contact.POSTALCODE || '',
    city: contact.city || contact.CITY || '',
    country: contact.country || contact.COUNTRY || '',
    countryCode: contact.countrycode || contact.countryCode || contact.COUNTRYCODE || '',
    latitude: contact.latitude || contact.LATITUDE || null,
    longitude: contact.longitude || contact.LONGITUDE || null,
    googlePlaceId: contact.googleplaceid || contact.googlePlaceId || contact.GOOGLEPLACEID || '',
    googleUrl: contact.googleurl || contact.googleUrl || contact.GOOGLEURL || '',
    nationality: contact.nationality || contact.NATIONALITY || '',
    // Profile type and employment status
    profileTypeId: contact.profiletypeid || contact.profileTypeId || contact.PROFILETYPEID || null,
    profileTypeCode: contact.profiletypecode || contact.profileTypeCode || contact.PROFILETYPECODE || '',
    profileTypeName_fr: contact.profiletypename_fr || contact.profileTypeName_fr || contact.PROFILETYPENAME_FR || '',
    profileTypeName_en: contact.profiletypename_en || contact.profileTypeName_en || contact.PROFILETYPENAME_EN || '',
    employmentStatus: contact.employmentstatus || contact.employmentStatus || contact.EMPLOYMENTSTATUS || '',
    // Occupation (ESCO)
    occupationId: contact.occupationid || contact.occupationId || contact.OCCUPATIONID || '',
    occupationLabel_fr: contact.occupationlabel_fr || contact.occupationLabel_fr || contact.OCCUPATIONLABEL_FR || '',
    occupationLabel_en: contact.occupationlabel_en || contact.occupationLabel_en || contact.OCCUPATIONLABEL_EN || '',
    // Financial/Business fields
    tvaNum: contact.tvanum || contact.tvaNum || contact.TVANUM || '',
    bankName: contact.bankname || contact.bankName || contact.BANKNAME || '',
    bankNum: contact.banknum || contact.bankNum || contact.BANKNUM || '',
    reputation: contact.reputation || contact.REPUTATION || 0,
    // Optional fields for private contacts (stored in localStorage)
    homeNeeds: contact.homeNeeds || contact.homeneeds || [],
    accessCode: contact.accessCode || contact.accesscode || '',
    notes: contact.notes || contact.NOTES || '',
    // Sync-related fields (set by syncContactsWithDB)
    profileRole: contact.profileRole || contact.PROFILEROLE || contact.role || contact.ROLE || '',
    profileDeleted: contact.profileDeleted || contact.PROFILEDELETED || false,
    profileReassociated: contact.profileReassociated || contact.PROFILEREASSOCIATED || false
});

/**
 * Check if a private contact has valid data (firstName or lastName)
 * @param {Object} contact - Contact object
 * @returns {boolean} True if contact has valid data
 */
const hasValidContactData = (contact) => {
    // Public contacts (with peopleuuid) are always valid - data comes from peoples table
    if (contact.peopleuuid || contact.PEOPLEUUID) {
        return true;
    }
    // Private contacts must have at least firstName or lastName
    const firstName = contact.firstName || contact.firstname || contact.FIRSTNAME || '';
    const lastName = contact.lastName || contact.lastname || contact.LASTNAME || '';
    return firstName.trim().length > 0 || lastName.trim().length > 0;
};

/**
 * Clean up invalid private contacts (no firstName/lastName and no peopleuuid)
 * @param {number} userId - User's ID
 * @param {Array} contacts - Contacts to clean
 * @returns {Promise<Array>} Cleaned contacts array
 */
const cleanupInvalidContacts = async (userId, contacts) => {
    const validContacts = [];
    const invalidContactIds = [];

    for (const contact of contacts) {
        if (hasValidContactData(contact)) {
            validContacts.push(contact);
        } else {
            console.log('Found invalid contact (no data):', contact.id, contact.localuuid);
            if (contact.id) {
                invalidContactIds.push(contact.id);
            }
        }
    }

    // Delete invalid contacts from database
    if (invalidContactIds.length > 0) {
        console.log('Cleaning up', invalidContactIds.length, 'invalid contacts...');
        for (const contactId of invalidContactIds) {
            try {
                await fetch(`${API_BASE_URL}/${contactId}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                    credentials: 'include'
                });
                console.log('Deleted invalid contact:', contactId);
            } catch (error) {
                console.error('Error deleting invalid contact:', contactId, error);
            }
        }
    }

    return validContacts;
};

/**
 * Get all contacts for a user from API
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh from API (skip cache)
 * @returns {Promise<Array>} Array of contact objects
 */
export const getUserContacts = async (peopleId, forceRefresh = false) => {
    // Return from cache if available and not forcing refresh
    if (!forceRefresh && contactsCache[peopleId]) {
        console.log('Returning cached contacts for peopleId:', peopleId);

        // Ensure localStorage is synced with cache
        const localData = getLocalContactsWithTimestamp(peopleId);
        if (localData.contacts.length !== contactsCache[peopleId].length) {
            console.log('Cache/localStorage mismatch - syncing...');
            const timestamp = new Date().toISOString();
            saveLocalContacts(peopleId, contactsCache[peopleId], timestamp);

            // Also sync to server
            const storageInfo = getCurrentUserStorageInfo();
            if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                syncContactsToServerDebounced(peopleId);
            }
        }

        return contactsCache[peopleId];
    }

    try {
        console.log('Fetching contacts from API for peopleId:', peopleId);
        const response = await fetch(`${API_BASE_URL}/owner/${peopleId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        console.log('API response:', result);

        const success = result.success || result.SUCCESS;
        const data = result.data || result.DATA || [];

        if (success || data.length > 0) {
            // Get local contacts to merge private contact data
            let localContacts = getLocalContacts(peopleId);

            // If localStorage is empty, try to load from server file first
            if (localContacts.length === 0) {
                const storageInfo = getCurrentUserStorageInfo();
                if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                    console.log('localStorage empty, loading from server file...');
                    const serverContacts = await loadContactsFromFile(storageInfo.peopleuuid, storageInfo.storageKey);
                    if (serverContacts && serverContacts.length > 0) {
                        console.log('Loaded', serverContacts.length, 'contacts from server file');
                        localContacts = serverContacts;
                        // Save to localStorage for future use
                        saveLocalContacts(peopleId, serverContacts);
                    }
                }
            }

            const localContactsMap = new Map(localContacts.map(c => [c.localuuid, c]));

            // Normalize and merge with local data
            let contacts = data.map(apiContact => {
                const normalized = normalizeContact(apiContact);
                // Merge with local data if exists (for private contacts)
                const localData = localContactsMap.get(normalized.localuuid);
                if (localData && !normalized.peopleuuid) {
                    // Private contact - use local data for fields not in DB
                    return {
                        ...normalized,
                        // Basic info
                        firstName: localData.firstName || normalized.firstName,
                        lastName: localData.lastName || normalized.lastName,
                        sexe: localData.sexe || normalized.sexe,
                        email: localData.email || normalized.email,
                        phone: localData.phone || normalized.phone,
                        phoneBureau: localData.phoneBureau || normalized.phoneBureau,
                        gsm: localData.gsm || normalized.gsm,
                        company: localData.company || normalized.company,
                        position: localData.position || normalized.position,
                        // Address fields (detailed)
                        googleAddress: localData.googleAddress || normalized.googleAddress,
                        street: localData.street || normalized.street,
                        number: localData.number || normalized.number,
                        box: localData.box || normalized.box,
                        postalCode: localData.postalCode || normalized.postalCode,
                        city: localData.city || normalized.city,
                        country: localData.country || normalized.country,
                        countryCode: localData.countryCode || normalized.countryCode,
                        latitude: localData.latitude || normalized.latitude,
                        longitude: localData.longitude || normalized.longitude,
                        googlePlaceId: localData.googlePlaceId || normalized.googlePlaceId,
                        googleUrl: localData.googleUrl || normalized.googleUrl,
                        nationality: localData.nationality || normalized.nationality,
                        // Professional fields
                        profileTypeId: localData.profileTypeId || normalized.profileTypeId,
                        occupationId: localData.occupationId || normalized.occupationId,
                        occupationLabel_fr: localData.occupationLabel_fr || normalized.occupationLabel_fr,
                        occupationLabel_en: localData.occupationLabel_en || normalized.occupationLabel_en,
                        employmentStatus: localData.employmentStatus || normalized.employmentStatus,
                        // Financial/Business fields
                        tvaNum: localData.tvaNum || normalized.tvaNum,
                        bankName: localData.bankName || normalized.bankName,
                        bankNum: localData.bankNum || normalized.bankNum,
                        reputation: localData.reputation || normalized.reputation,
                        // Other fields
                        homeNeeds: localData.homeNeeds || [],
                        accessCode: localData.accessCode || '',
                        notes: localData.notes || '',
                        description: localData.description || normalized.description
                    };
                }
                return normalized;
            });

            // Clean up invalid private contacts (no data and no peopleuuid)
            contacts = await cleanupInvalidContacts(peopleId, contacts);

            console.log('Normalized contacts:', contacts);

            // Update cache
            contactsCache[peopleId] = contacts;

            // ALWAYS save to localStorage
            console.log('=== ABOUT TO SAVE TO LOCALSTORAGE ===');
            const timestamp = new Date().toISOString();
            saveLocalContacts(peopleId, contacts, timestamp);

            // Sync to server file (not debounced - immediate for initial load)
            const storageInfo = getCurrentUserStorageInfo();
            if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                console.log('=== SYNCING TO SERVER FILE ===');
                try {
                    const result = await saveContactsToFile(
                        storageInfo.peopleuuid,
                        storageInfo.storageKey,
                        contacts,
                        timestamp
                    );
                    console.log('Server file sync result:', result);
                } catch (syncError) {
                    console.error('Server file sync error:', syncError);
                }
            } else {
                console.warn('Cannot sync to server - missing storageInfo:', storageInfo);
            }

            return contacts;
        }

        return [];
    } catch (error) {
        console.error('Error fetching contacts from API:', error);
        // Fallback to localStorage
        return getLocalContacts(peopleId);
    }
};

/**
 * Get visible contacts (excluding those with contactType = 'PRIVATE')
 * Use this in modules other than Contacts to hide private contacts from search results etc.
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh from API (skip cache)
 * @returns {Promise<Array>} Array of visible contact objects
 */
export const getVisibleUserContacts = async (peopleId, forceRefresh = false) => {
    const allContacts = await getUserContacts(peopleId, forceRefresh);
    return allContacts.filter(contact => contact.contactType !== 'PRIVATE');
};

/**
 * Get contact IDs for a user (for backward compatibility)
 * @param {number} userId - User's ID (owner)
 * @returns {Array<string>} Array of contact localuuids
 */
export const getUserContactIds = (userId) => {
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.map(c => c.localuuid || c.id);
};

/**
 * Find existing people for contact creation
 * Searches by (email OR phone OR gsm) AND firstName AND lastName
 * @param {Object} searchData - Search criteria
 * @returns {Promise<Object|null>} Found people or null
 */
export const findPeopleForContact = async (searchData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/find-people`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                firstName: searchData.firstName,
                lastName: searchData.lastName,
                email: searchData.email || '',
                phone: searchData.phone || '',
                gsm: searchData.gsm || ''
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.found) {
            return result.data;
        }

        return null;
    } catch (error) {
        console.error('Error finding people for contact:', error);
        return null;
    }
};

/**
 * Add a contact for user
 * @param {number} userId - User's ID (owner/createdById)
 * @param {Object} contactData - Contact data to add
 * @returns {Promise<Object>} Created contact or null
 */
export const addUserContact = async (userId, contactData) => {
    try {
        // Generate localuuid if not provided
        const localuuid = contactData.localuuid || generateUUID();

        // Prepare payload for API
        const payload = {
            localuuid: localuuid,
            peopleuuid: contactData.peopleuuid || '',
            createdById: userId,
            rating: contactData.rating || 0
        };

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        const success = result.success || result.SUCCESS;
        const data = result.data || result.DATA;

        if (success) {
            // Build full contact object with all fields
            const newContact = {
                ...normalizeContact(data || {}),
                localuuid: localuuid,
                peopleuuid: contactData.peopleuuid || null,
                // Basic info
                firstName: contactData.firstName || '',
                lastName: contactData.lastName || '',
                sexe: contactData.sexe || '',
                email: contactData.email || '',
                phone: contactData.phone || '',
                phoneBureau: contactData.phoneBureau || '',
                gsm: contactData.gsm || '',
                company: contactData.company || '',
                position: contactData.position || '',
                // Address fields (detailed)
                googleAddress: contactData.googleAddress || '',
                street: contactData.street || '',
                number: contactData.number || '',
                box: contactData.box || '',
                postalCode: contactData.postalCode || '',
                city: contactData.city || '',
                country: contactData.country || '',
                countryCode: contactData.countryCode || '',
                latitude: contactData.latitude || null,
                longitude: contactData.longitude || null,
                googlePlaceId: contactData.googlePlaceId || '',
                googleUrl: contactData.googleUrl || '',
                nationality: contactData.nationality || '',
                // Professional fields
                profileTypeId: contactData.profileTypeId || null,
                occupationId: contactData.occupationId || '',
                occupationLabel_fr: contactData.occupationLabel_fr || '',
                occupationLabel_en: contactData.occupationLabel_en || '',
                employmentStatus: contactData.employmentStatus || '',
                // Financial/Business fields
                tvaNum: contactData.tvaNum || '',
                bankName: contactData.bankName || '',
                bankNum: contactData.bankNum || '',
                reputation: contactData.reputation || 0,
                // Other fields
                homeNeeds: contactData.homeNeeds || [],
                accessCode: contactData.accessCode || '',
                notes: contactData.notes || '',
                description: contactData.description || '',
                contactType: contactData.peopleuuid ? 'PUBLIC' : 'CONTACT'
            };

            // Update cache
            if (!contactsCache[userId]) {
                contactsCache[userId] = [];
            }
            contactsCache[userId].push(newContact);
            saveLocalContacts(userId, contactsCache[userId]);

            // Sync to server (debounced)
            syncContactsToServerDebounced(userId);

            return newContact;
        }

        throw new Error(result.message || result.MESSAGE || 'Failed to add contact');
    } catch (error) {
        console.error('Error adding contact:', error);
        return null;
    }
};

/**
 * Add a contact from an existing people record
 * @param {number} userId - User's ID (owner/createdById)
 * @param {Object} people - People object to add as contact
 * @returns {Promise<Object>} Created contact or null
 */
export const addPeopleAsContact = async (userId, people) => {
    const peopleuuid = people.peopleUuid || people.peopleuuid || people.PEOPLEUUID;
    return addUserContact(userId, {
        peopleuuid: peopleuuid,
        // Basic info
        firstName: people.firstName || people.firstname,
        lastName: people.lastName || people.lastname,
        sexe: people.sexe || people.SEXE,
        email: people.email,
        phone: people.phone,
        phoneBureau: people.phoneBureau || people.phonebureau,
        gsm: people.gsm,
        company: people.company,
        position: people.position,
        // Address fields
        googleAddress: people.googleAddress || people.googleaddress,
        street: people.street,
        number: people.number,
        box: people.box,
        postalCode: people.postalCode || people.postalcode,
        city: people.city,
        country: people.country,
        countryCode: people.countryCode || people.countrycode,
        latitude: people.latitude,
        longitude: people.longitude,
        googlePlaceId: people.googlePlaceId || people.googleplaceid,
        googleUrl: people.googleUrl || people.googleurl,
        nationality: people.nationality,
        // Professional fields
        profileTypeId: people.profileTypeId || people.profiletypeid,
        occupationId: people.occupationId || people.occupationid,
        occupationLabel_fr: people.occupationLabel_fr || people.occupationlabel_fr,
        occupationLabel_en: people.occupationLabel_en || people.occupationlabel_en,
        employmentStatus: people.employmentStatus || people.employmentstatus,
        // Financial/Business fields
        tvaNum: people.tvaNum || people.tvanum,
        bankName: people.bankName || people.bankname,
        bankNum: people.bankNum || people.banknum,
        reputation: people.reputation,
        // Other
        description: people.description
    });
};

/**
 * Remove a contact
 * @param {number} peopleId - User's ID (owner)
 * @param {number} contactId - Contact ID to remove
 * @returns {Promise<boolean>} Success status
 */
export const removeUserContact = async (peopleId, contactId) => {
    try {
        console.log('=== REMOVE CONTACT ===');
        console.log('peopleId:', peopleId);
        console.log('contactId:', contactId);

        if (!contactId) {
            console.error('removeUserContact: contactId is missing or invalid');
            return false;
        }

        const response = await fetch(`${API_BASE_URL}/${contactId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        console.log('DELETE response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('DELETE failed:', response.status, errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        // Parse response to verify success
        const result = await response.json();
        console.log('DELETE response body:', result);

        const success = result.success || result.SUCCESS;
        if (!success) {
            console.error('DELETE returned success=false:', result.message || result.MESSAGE);
            return false;
        }

        console.log('Contact deleted from DB successfully');

        // Initialize cache from localStorage if empty
        if (!contactsCache[peopleId]) {
            contactsCache[peopleId] = getLocalContacts(peopleId);
        }

        // Update cache and localStorage
        contactsCache[peopleId] = contactsCache[peopleId].filter(c => c.id !== contactId);
        saveLocalContacts(peopleId, contactsCache[peopleId]);

        // Sync to server (debounced)
        syncContactsToServerDebounced(peopleId);

        return true;
    } catch (error) {
        console.error('Error removing contact:', error);
        return false;
    }
};

/**
 * Update a contact
 * @param {number} contactId - Contact ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated contact or null
 */
export const updateContact = async (contactId, updates) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${contactId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
            return normalizeContact(result.data);
        }

        return null;
    } catch (error) {
        console.error('Error updating contact:', error);
        return null;
    }
};

/**
 * Update contact rating
 * @param {number} contactId - Contact ID
 * @param {number} rating - Rating (0-5)
 * @param {number} userId - User ID (optional, for cache sync)
 * @returns {Promise<boolean>} Success status
 */
export const setContactRating = async (contactId, rating, userId = null) => {
    const result = await updateContact(contactId, { rating: Math.max(0, Math.min(5, rating)) });

    // Update cache and sync to server if userId provided
    if (result !== null && userId) {
        if (contactsCache[userId]) {
            const contactIndex = contactsCache[userId].findIndex(c => c.id === contactId);
            if (contactIndex !== -1) {
                contactsCache[userId][contactIndex].rating = Math.max(0, Math.min(5, rating));
                saveLocalContacts(userId, contactsCache[userId]);
                syncContactsToServerDebounced(userId);
            }
        }
    }

    return result !== null;
};

/**
 * Update a contact in local cache and sync to server
 * Use this for local-only updates that need to be persisted
 * @param {number} userId - User ID
 * @param {string} localuuid - Contact localuuid
 * @param {Object} updates - Fields to update
 */
export const updateLocalContact = (userId, localuuid, updates) => {
    if (!contactsCache[userId]) {
        contactsCache[userId] = getLocalContacts(userId);
    }

    const contactIndex = contactsCache[userId].findIndex(c => c.localuuid === localuuid);
    if (contactIndex !== -1) {
        contactsCache[userId][contactIndex] = {
            ...contactsCache[userId][contactIndex],
            ...updates
        };
        saveLocalContacts(userId, contactsCache[userId]);
        syncContactsToServerDebounced(userId);
        return true;
    }

    return false;
};

/**
 * Check if a people is in user's contacts
 * @param {number} userId - User's ID (owner)
 * @param {string} targetPeopleUuid - People UUID to check
 * @returns {boolean} True if contact exists
 */
export const isUserContact = (userId, targetPeopleUuid) => {
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.some(c => c.peopleuuid === targetPeopleUuid);
};

/**
 * Check if a contact exists by localuuid
 * @param {number} userId - User's ID (owner)
 * @param {string} localuuid - Contact localuuid to check
 * @returns {boolean} True if contact exists
 */
export const isContactByLocalUuid = (userId, localuuid) => {
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.some(c => c.localuuid === localuuid);
};

/**
 * Get contact by peopleuuid
 * @param {number} userId - User's ID (owner)
 * @param {string} targetPeopleUuid - People UUID to find
 * @returns {Object|null} Contact or null
 */
export const getContactByPeopleUuid = (userId, targetPeopleUuid) => {
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.find(c => c.peopleuuid === targetPeopleUuid) || null;
};

/**
 * Get contact by localuuid
 * @param {number} userId - User's ID (owner)
 * @param {string} localuuid - Contact localuuid to find
 * @returns {Object|null} Contact or null
 */
export const getContactByLocalUuid = (userId, localuuid) => {
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.find(c => c.localuuid === localuuid) || null;
};

/**
 * Get count of user's contacts
 * @param {number} peopleId - User's ID
 * @returns {number} Number of contacts
 */
export const getUserContactsCount = (peopleId) => {
    const contacts = contactsCache[peopleId] || getLocalContacts(peopleId);
    return contacts.length;
};

/**
 * Clear cache (useful on logout)
 * @param {number} peopleId - User's ID (optional)
 */
export const clearContactsCache = (peopleId = null) => {
    if (peopleId) {
        delete contactsCache[peopleId];
    } else {
        contactsCache = {};
    }
};

/**
 * Preload contacts for a user
 * @param {number} peopleId - User's ID
 */
export const preloadContacts = async (peopleId) => {
    await getUserContacts(peopleId);
};

/**
 * Backup contacts file on server before saving
 * @param {string} peopleuuid - Owner's peopleUuid
 * @returns {Promise<Object>} Result with success status
 */
export const backupContactsFile = async (peopleuuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/backup-file`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ peopleuuid })
        });

        if (!response.ok) {
            console.warn('Backup file warning:', response.status);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        console.log('Contacts backup result:', result.success ? 'OK' : result.message);
        return result;
    } catch (error) {
        console.warn('Error backing up contacts file:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Save contacts to encrypted file on server with timestamp
 * Creates a backup of the existing file before saving
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Encryption key
 * @param {Array} contacts - Contacts to save
 * @param {string} lastModified - ISO timestamp
 * @returns {Promise<Object>} Result with success status
 */
export const saveContactsToFile = async (peopleuuid, storageKey, contacts, lastModified = null) => {
    try {
        // Create backup before saving (don't block on failure)
        backupContactsFile(peopleuuid).catch(err => console.warn('Backup failed:', err));

        const timestamp = lastModified || new Date().toISOString();
        const response = await fetch(`${API_BASE_URL}/save-file`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleuuid,
                storageKey,
                contacts,
                lastModified: timestamp
            })
        });

        if (!response.ok) {
            const text = await response.text();
            console.error('Save file error response:', text);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error saving contacts to file:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Load contacts from encrypted file on server with timestamp
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Object>} { contacts: Array, lastModified: string|null }
 */
export const loadContactsFromFileWithTimestamp = async (peopleuuid, storageKey) => {
    try {
        console.log('=== loadContactsFromFile ===');
        console.log('peopleuuid:', peopleuuid);
        console.log('storageKey:', storageKey ? storageKey.substring(0, 16) + '...' : 'MISSING');

        const response = await fetch(`${API_BASE_URL}/load-file`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleuuid,
                storageKey
            })
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Load file error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const text = await response.text();
        console.log('Raw response:', text.substring(0, 500));

        let result;
        try {
            result = JSON.parse(text);
        } catch (parseErr) {
            console.error('JSON parse error:', parseErr);
            return { contacts: [], lastModified: null };
        }

        console.log('Parsed result keys:', Object.keys(result));
        const success = result.success || result.SUCCESS;
        const contacts = result.contacts || result.CONTACTS || [];
        const lastModified = result.lastModified || result.LASTMODIFIED || null;

        console.log('success:', success);
        console.log('contacts count:', contacts.length);
        console.log('lastModified:', lastModified);

        return success ? { contacts, lastModified } : { contacts: [], lastModified: null };
    } catch (error) {
        console.error('Error loading contacts from file:', error);
        return { contacts: [], lastModified: null };
    }
};

/**
 * Load contacts from encrypted file on server (backward compatible)
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Array>} Contacts array
 */
export const loadContactsFromFile = async (peopleuuid, storageKey) => {
    const result = await loadContactsFromFileWithTimestamp(peopleuuid, storageKey);
    return result.contacts;
};

// Backward compatibility - deprecated functions
export const getUserContactIds_deprecated = (userId) => {
    console.warn('getUserContactIds is deprecated, use getUserContacts instead');
    return getUserContactIds(userId);
};

export const getContactByPeopleId = (userId, targetPeopleId) => {
    console.warn('getContactByPeopleId is deprecated, use getContactByPeopleUuid instead');
    const contacts = contactsCache[userId] || getLocalContacts(userId);
    return contacts.find(c => c.id === targetPeopleId) || null;
};
